import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  useTheme,
  useMediaQuery,
  alpha,
  Slide
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import CallNowButton from '../../../components/shared/CallNowButton';

/**
 * Modern Floating Mobile CTA
 * Appears after scrolling past hero section
 * Call and Quote buttons shown immediately when scrolling (no FAB to open).
 */
const FloatingMobileCta = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isMobile) return;

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const heroHeight = window.innerHeight * 0.8;
      if (scrollY > heroHeight) setShow(true);
      else if (scrollY < 100) setShow(false);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  if (!isMobile) return null;


  return (
    <Slide direction="up" in={show} timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 20, sm: 24 },
          right: { xs: 16, sm: 24 },
          zIndex: (t) => t.zIndex.speedDial,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1.25,
        }}
      >
        <Button
          variant="contained"
          size="medium"
          endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
          onClick={() => navigate('/quote')}
          sx={{
            minWidth: 172,
            py: 1.5,
            px: 3,
            fontSize: '0.9375rem',
            fontWeight: 700,
            letterSpacing: '0.02em',
            borderRadius: '999px',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}, 0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            border: 'none',
            '@media (hover: hover)': {
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}, 0 12px 28px ${alpha(theme.palette.primary.main, 0.3)}`,
                transform: 'translateY(-1px)',
              },
            },
            '@media (hover: none)': {
              '&:active': {
                transform: 'scale(0.98)',
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
              }
            },
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          Get Instant Quote
        </Button>
        <CallNowButton
          size="medium"
          sx={{
            minWidth: 172,
            py: 1.5,
            px: 3,
            fontSize: '0.9375rem',
            fontWeight: 600,
            letterSpacing: '0.02em',
            borderRadius: '999px',
            border: 'none',
            color: 'white',
            background: `linear-gradient(135deg, ${theme.palette.warning?.main || '#f59e0b'} 0%, ${theme.palette.warning?.dark || '#d97706'} 100%)`,
            boxShadow: `0 2px 8px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.35)}, 0 8px 24px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.25)}`,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            '@media (hover: hover)': {
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.warning?.dark || '#d97706'} 0%, ${theme.palette.warning?.main || '#f59e0b'} 100%)`,
                boxShadow: `0 4px 12px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.4)}, 0 12px 28px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.3)}`,
                transform: 'translateY(-1px)',
              },
            },
            '@media (hover: none)': {
              '&:active': {
                transform: 'scale(0.98)',
                boxShadow: `0 2px 8px ${alpha(theme.palette.warning?.main || '#f59e0b', 0.3)}`,
              }
            },
            transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        />
      </Box>
    </Slide>
  );
};

export default FloatingMobileCta;
